import type { Camera } from '../engine/Camera';
import type { Powerup } from '../entities/Powerup';
import type { Snake } from '../entities/Snake';
import { WORLD } from '../constants';

/**
 * Minimap — screen-space radar rendered into the bottom-right corner of the
 * main canvas. Shows:
 *   - World border framed with the team neon accent.
 *   - Every alive snake's head as a colored dot, sized by mass.
 *   - The player as a brighter, ringed dot.
 *   - The player's current viewport as a translucent frustum rectangle.
 *
 * Cost: O(snakes) per frame, no allocations after construction. Cheap.
 */
export class Minimap {
  /** CSS-pixel side length of the minimap square. */
  private readonly size: number;
  /** Padding from the bottom-right corner, in CSS pixels. */
  private readonly pad: number;

  constructor(size = 168, pad = 18) {
    this.size = size;
    this.pad = pad;
  }

  /**
   * @param time Seconds since game start — drives the leader pulse so the
   *   ring breathes at a fixed cadence regardless of fps.
   * @param powerups Active arena powerups; rendered as 5px hue-coded dots
   *   so the player can plan a route toward a rare drop without missing
   *   it offscreen.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    snakes: Snake[],
    player: Snake,
    time: number = 0,
    powerups: Powerup[] = [],
    lightMode: boolean = false,
  ): void {
    const vw = camera.viewportW;
    const vh = camera.viewportH;
    const s = this.size;
    const x = vw - s - this.pad;
    const y = vh - s - this.pad;

    // World → minimap scale.
    const scale = s / WORLD.SIZE;

    // --- Frame ---
    ctx.save();
    // Background — inverted in light mode so the minimap stays as a
    // contrast "card" against the bright world. Dark mode keeps the
    // existing near-black panel.
    ctx.fillStyle = lightMode
      ? 'rgba(255, 255, 255, 0.82)'
      : 'rgba(8, 11, 22, 0.78)';
    ctx.strokeStyle = lightMode
      ? 'rgba(26, 31, 43, 0.18)'
      : 'rgba(255, 255, 255, 0.10)';
    ctx.lineWidth = 1;
    Minimap.roundRect(ctx, x, y, s, s, 10);
    ctx.fill();
    ctx.stroke();

    // Inner subtle grid (4 divisions).
    ctx.strokeStyle = lightMode
      ? 'rgba(26, 31, 43, 0.08)'
      : 'rgba(120, 180, 255, 0.07)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 1; i < 4; i++) {
      const fx = x + (s * i) / 4;
      const fy = y + (s * i) / 4;
      ctx.moveTo(fx, y);
      ctx.lineTo(fx, y + s);
      ctx.moveTo(x, fy);
      ctx.lineTo(x + s, fy);
    }
    ctx.stroke();

    // Viewport rectangle (player's current view in world coords, mapped to minimap).
    const bounds = camera.getVisibleBounds();
    const vx = x + bounds.minX * scale;
    const vy = y + bounds.minY * scale;
    const vWidth = (bounds.maxX - bounds.minX) * scale;
    const vHeight = (bounds.maxY - bounds.minY) * scale;
    ctx.strokeStyle = `hsla(${player.teamHue} 100% 70% / 0.55)`;
    ctx.lineWidth = 1.2;
    ctx.strokeRect(
      Math.max(x, vx),
      Math.max(y, vy),
      Math.min(s, vWidth),
      Math.min(s, vHeight),
    );

    // Powerup pickups — small hue-coded dots so the player can plan a
    // route to a rare drop. Drawn BEFORE snake dots so a snake on top of
    // a powerup eclipses it (matches in-game pickup priority).
    if (powerups.length > 0) {
      const pulse = 0.6 + 0.4 * Math.sin(time * 4);
      for (let i = 0; i < powerups.length; i++) {
        const p = powerups[i]!;
        const px = x + p.x * scale;
        const py = y + p.y * scale;
        const hue = p.kind === 'surge' ? 195 : p.kind === 'shield' ? 320 : 48;
        const color = `hsl(${hue} 100% 65%)`;
        ctx.save();
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = 6;
        ctx.globalAlpha = 0.55 + 0.4 * pulse;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
        // Thin outer ring to make sure tiny dots aren't lost to
        // background noise on dense maps.
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Snake dots — non-player first (so player draws on top).
    for (let i = 0; i < snakes.length; i++) {
      const sn = snakes[i]!;
      if (!sn.alive || sn.isPlayer) continue;
      this.drawDot(ctx, x, y, scale, sn, false);
      // Leader spotlight — a faint pulsing ring around the #1 snake so
      // the player always knows where the alpha is on the minimap.
      if (sn.isLeader) {
        const px = x + sn.head.x * scale;
        const py = y + sn.head.y * scale;
        const pulse = 0.55 + 0.45 * Math.sin(time * 3);
        const color = `hsl(${sn.teamHue} 100% 70%)`;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.5 + 0.4 * pulse;
        ctx.lineWidth = 1.3;
        ctx.shadowColor = color;
        ctx.shadowBlur = 7;
        ctx.beginPath();
        ctx.arc(px, py, 6 + 2 * pulse, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }
    if (player.alive) this.drawDot(ctx, x, y, scale, player, true);

    // Label.
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = '10px "JetBrains Mono", ui-monospace, monospace';
    ctx.textBaseline = 'top';
    ctx.fillText('MAP · 5K', x + 8, y + 6);

    ctx.restore();
  }

  private drawDot(
    ctx: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    scale: number,
    snake: Snake,
    isPlayer: boolean,
  ): void {
    const dx = ox + snake.head.x * scale;
    const dy = oy + snake.head.y * scale;
    // Mass-scaled dot — bigger snakes show bigger blips.
    const r = isPlayer ? 3.5 : Math.min(3, 1.4 + Math.log10(Math.max(1, snake.mass / 200)) * 0.9);
    const color = `hsl(${snake.teamHue} 100% 65%)`;
    if (isPlayer) {
      // Ring + center dot.
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.arc(dx, dy, r + 2.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    } else {
      ctx.shadowBlur = 0;
    }
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(dx, dy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  private static roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }
}
